import { exerciseNow, setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useMemo, useState } from "react";
import type { ZahlenZerlegenExerciseConfig, ZahlenZerlegenRunSettings } from "@calega/content";
import type { ZahlenZerlegenRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { ExerciseDurationSelect } from "./ExerciseSettings";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type Phase = "running" | "feedback" | "complete";
interface Answer { itemId: string; selectedIndices: number[]; sum: number; correct: boolean }
interface State { runId: string; run: ZahlenZerlegenRun; index: number; phase: Phase; endsAt: number | null; answers: Answer[]; result: { score: number; maxScore: number } | null; selectedIndices: number[] }

const ranges: Array<{ id: ZahlenZerlegenRunSettings["range"]; label: string }> = [
  { id: "0-10", label: "0–10" }, { id: "0-20", label: "0–20" }, { id: "20-50", label: "20–50" },
  { id: "20-100", label: "20–100" }, { id: "100-200", label: "100–200" }, { id: "100-1000", label: "100–1.000" }, { id: "1000-100000", label: "1.000–100.000" },
];

export function canAdvanceZahlenZerlegen(phase: Phase, canControl: boolean) {
  return phase === "feedback" && canControl;
}

export function ZahlenZerlegen({ account, config, pageId }: { account: Account; config: ZahlenZerlegenExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const follower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const canAnswer = !realtime.session || facilitator || follower;
  const [mode, setMode] = useState<ZahlenZerlegenRunSettings["mode"]>(config.defaultMode);
  const [count, setCount] = useState(config.defaultCount);
  const [durationSeconds, setDurationSeconds] = useState(config.defaultDurationSeconds);
  const [range, setRange] = useState<ZahlenZerlegenRunSettings["range"]>(config.defaultRange);
  const [exercise, setExercise] = useState<State | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [, tick] = useState(0);
  const score = useMemo(() => exercise?.answers.filter((answer) => answer.correct).length ?? 0, [exercise?.answers]);

  const publish = (state: State | null) => realtime.publishExercise({ exerciseType: "zahlen-zerlegen", pageId, state });

  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: State | null } | null;
    if (synced?.exerciseType === "zahlen-zerlegen" && synced.pageId === pageId) setExercise(synced.state ?? null);
  }, [realtime.state.exercise, pageId]);

  useEffect(() => {
    if (!exercise?.endsAt || exercise.phase === "complete") return;
    const interval = window.setInterval(() => tick((current) => current + 1), 250);
    return () => window.clearInterval(interval);
  }, [exercise?.endsAt, exercise?.phase]);

  useEffect(() => {
    if (busy || !exercise?.endsAt || exercise.phase === "complete" || !canControl) return;
    const timeout = setExerciseTimeout(() => finish().catch((error) => setMessage(error.message)), Math.max(0, exercise.endsAt - exerciseNow(realtime.serverNow)) + 20);
    return () => clearExerciseTimeout(timeout);
  }, [exercise?.endsAt, exercise?.phase, exercise?.index, exercise?.answers.length, canControl, busy]);

  const settings = (): ZahlenZerlegenRunSettings => ({ mode, count, durationSeconds, range });

  async function start(runSettings = settings()) {
    if (busy || !canControl) return;
    setBusy(true);
    try {
      const response = await startExerciseRun<{ runId: string; exercise: ZahlenZerlegenRun }>({ contentPageId: pageId, settings: runSettings });
      const next: State = { runId: response.runId, run: response.exercise, index: 0, phase: "running", endsAt: response.exercise.settings.mode === "time" ? exerciseNow(realtime.serverNow) + response.exercise.settings.durationSeconds * 1_000 : null, answers: [], result: null, selectedIndices: [] };
      setExercise(next); setMessage(""); publish(next);
    } finally { setBusy(false); }
  }

  async function abort() { if (exercise && exercise.phase !== "complete") await abortExerciseRun(exercise.runId); }

  async function backToSettings() {
    if (!exercise || busy || !canControl) return;
    setBusy(true);
    try {
      await abort();
      setMode(exercise.run.settings.mode); setCount(exercise.run.settings.count); setDurationSeconds(exercise.run.settings.durationSeconds); setRange(exercise.run.settings.range);
      setExercise(null); setMessage(""); publish(null);
    } finally { setBusy(false); }
  }

  async function remix() {
    if (!exercise || busy || !canControl) return;
    const runSettings = exercise.run.settings;
    setBusy(true);
    try { await abort(); } finally { setBusy(false); }
    await start(runSettings);
  }

  function toggle(index: number) {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const selectedIndices = exercise.selectedIndices.includes(index) ? exercise.selectedIndices.filter((item) => item !== index) : [...exercise.selectedIndices, index];
    const next = { ...exercise, selectedIndices };
    setExercise(next); publish(next);
  }

  async function check() {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer || !exercise.selectedIndices.length) return;
    const item = exercise.run.items[exercise.index];
    if (!item) return;
    setBusy(true);
    try {
      await submitExerciseAnswer(exercise.runId, { type: "zahlen-zerlegen", itemId: item.id, selectedIndices: exercise.selectedIndices });
      const sum = exercise.selectedIndices.reduce((total, index) => total + item.numbers[index]!, 0);
      const next: State = { ...exercise, phase: "feedback", answers: [...exercise.answers, { itemId: item.id, selectedIndices: exercise.selectedIndices, sum, correct: sum === item.target }] };
      setExercise(next); publish(next);
    } finally { setBusy(false); }
  }

  async function finish() {
    if (!exercise || exercise.phase === "complete" || busy || !canControl) return;
    await completion.complete(exercise.runId, (result) => {
      const next = { ...exercise, result, phase: "complete" as const };
      setExercise(next); publish(next); setMessage("");
    });
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!exercise || exercise.phase !== "complete") return;
    if (repeat) await remix();
    else await backToSettings();
  }

  async function next() {
    if (!exercise || exercise.phase !== "feedback" || busy || !canControl) return;
    if ((exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length) return finish();
    const next = { ...exercise, index: exercise.index + 1, phase: "running" as const, selectedIndices: [] };
    setExercise(next); publish(next);
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (follower && !exercise) return <ExerciseWaitingCard className="zahlen-zerlegen-exercise" />;

  if (!exercise) return <section className="zahlen-zerlegen-exercise">
    <p className="quiz-instructions learning-text">{config.instructions}</p>
    <div className="mathe-settings zahlen-zerlegen-settings">
      <fieldset><legend>Zahlenraum</legend><div className="mathe-option-grid">{ranges.map((item) => <label className="checkbox-label" key={item.id}><input type="radio" name={`${pageId}-decomposition-range`} checked={range === item.id} onChange={() => setRange(item.id)} />{item.label}</label>)}</div></fieldset>
      <fieldset><legend>Durchlauf</legend><div className="mathe-run-settings"><div className="blitz-choice-row"><label className="checkbox-label"><input type="radio" name={`${pageId}-decomposition-mode`} checked={mode === "count"} onChange={() => setMode("count")} />Feste Anzahl</label><label className="checkbox-label"><input type="radio" name={`${pageId}-decomposition-mode`} checked={mode === "time"} onChange={() => setMode("time")} />Auf Zeit</label></div>{mode === "count" ? <label>Anzahl Aufgaben<select value={count} onChange={(event) => setCount(Number(event.target.value))}>{[5, 10, 15, 20].map((value) => <option value={value} key={value}>{value}</option>)}</select></label> : <ExerciseDurationSelect value={durationSeconds} onChange={setDurationSeconds} />}</div></fieldset>
    </div>
    <button className="primary" disabled={busy || !canControl} onClick={() => start().catch((error) => setMessage(error.message))}>Zahlenzerlegung starten</button>
    {message && <div className="alert info">{message}</div>}
  </section>;

  const item = exercise.run.items[exercise.index];
  const lastAnswer = exercise.answers.at(-1);
  const remaining = exercise.endsAt ? Math.max(0, Math.ceil((exercise.endsAt - exerciseNow(realtime.serverNow)) / 1_000)) : null;
  const last = (exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length;

  return <section className="zahlen-zerlegen-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu><button className="secondary small" disabled={busy} onClick={() => backToSettings().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button><button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button></ExerciseMenu>}
    <div className="exercise-progress">{exercise.run.settings.mode === "time" ? `${exercise.answers.length} beantwortet · ${remaining ?? 0} s` : `Aufgabe ${Math.min(exercise.index + 1, exercise.run.items.length)} von ${exercise.run.items.length}`}</div>
    {exercise.phase === "complete" ? <><div className="zahlen-zerlegen-result"><strong>{exercise.result?.score ?? score} von {exercise.result?.maxScore ?? exercise.answers.length} richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div>{canControl && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}{exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}</> : item && <>
      <div className="zahlen-zerlegen-card">
        <p className="zahlen-zerlegen-prompt learning-text">Kreise mindestens drei Zahlen ein, die zusammen die Zielzahl ergeben.</p>
        <div className="zahlen-zerlegen-target"><span>Zielzahl</span><strong>{item.target.toLocaleString("de-DE")}</strong></div>
        <div className="zahlen-zerlegen-numbers" aria-label={`Zahlen zur Zielzahl ${item.target}`}>
          {item.numbers.map((number, index) => <button key={`${index}-${number}`} className={`zahlen-zerlegen-number ${exercise.selectedIndices.includes(index) ? "selected" : ""} ${exercise.phase === "feedback" && exercise.selectedIndices.includes(index) ? lastAnswer?.correct ? "correct" : "wrong" : ""}`} disabled={exercise.phase !== "running" || busy || !canAnswer} onClick={() => toggle(index)}>{number.toLocaleString("de-DE")}</button>)}
        </div>
      </div>
      {exercise.phase === "feedback" && lastAnswer && <div className={`answer-feedback ${lastAnswer.correct ? "correct" : "wrong"}`}>{lastAnswer.correct ? "Richtig zerlegt!" : "Nicht ganz. Suche beim nächsten Mal mindestens drei passende Zahlen."}</div>}
      {exercise.phase === "running" && <div className="zahlen-zerlegen-actions"><button className="primary" disabled={busy || !canAnswer || exercise.selectedIndices.length < 3} onClick={() => check().catch((error) => setMessage(error.message))}>Überprüfen</button><button className="secondary" disabled={busy || !exercise.selectedIndices.length} onClick={() => { const next = { ...exercise, selectedIndices: [] }; setExercise(next); publish(next); }}>Auswahl zurücksetzen</button></div>}
      {canAdvanceZahlenZerlegen(exercise.phase, canControl) && <div className="zahlen-zerlegen-actions"><button className="primary" disabled={busy} onClick={() => next().catch((error) => setMessage(error.message))}>{last ? "Auswertung ansehen" : "Nächste Aufgabe"}</button></div>}
      {exercise.phase === "feedback" && !canControl && <p className="muted">Die nächste Aufgabe wird von der Sitzungsleitung geöffnet.</p>}
    </>}
    {message && <div className="alert info">{message}</div>}
  </section>;
}
